import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useGlobalsService } from '../services/useGlobals';
import { Variable } from '@restworld/utility-types';

export default function useVariables(variableNames: string | string[]) {
  const globalsService = useGlobalsService();

  const names = useMemo(() => {
    if (!variableNames) return [];
    return Array.isArray(variableNames) ? variableNames : [variableNames];
  }, [variableNames]);

  const queries = useMemo(
    () =>
      names.map((name) => ({
        queryKey: ['variableOptions', name],
        queryFn: () => globalsService.fetchVariableOptions(name),
        staleTime: Infinity,
        gcTime: Infinity,
        retry: 3,
        retryDelay: (attemptIndex: number) => (attemptIndex + 1) * 1000,
      })),
    [names, globalsService]
  );

  const results = useQueries({ queries });

  const loading = useMemo(() => results.some((result) => result.isLoading), [results]);

  const variableOptions = useMemo(() => {
    const options: Record<string, Variable[]> = {};
    results.forEach((result, index) => {
      if (result.data) {
        options[names[index]] = result.data.data;
      }
    });
    return options;
  }, [results, names]);

  return {
    loading,
    variableOptions,
  };
}
